/**
 * Play frame.
 *
 * SHELL-040 to SHELL-046 (goals HUD, pause/restart/quit, landscape layout) are
 * still open -- this wires the loader and the game contract and nothing else.
 * `Do not build a HUD, stars, or progression` (Sprint 1b) is why `onComplete`
 * leads to one plain message and a link rather than a result screen.
 *
 * The unknown-game branch (SHELL-013) is a plain check with no hooks before
 * it, for the same reason `LevelSelect` splits the same way: `gameId` and
 * `levelId` can change across renders of the same route without breaking the
 * rules of hooks, because the hook-using half lives in `PlayLoaded`.
 *
 * Resuming (`PROG-030`-`035`, `SHELL-017`) is decided once, the first render
 * `readyLevel` exists, and held in a ref rather than re-read every render:
 * this screen's own backgrounding handler writes to the same save document it
 * reads from, and re-deriving "is a level in progress" from a document this
 * screen itself keeps mutating would eventually see its own write and offer
 * to resume the level the player is already playing.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import type { GameContext, GameResult } from '@contracts/game-module';
import type { InProgress, SaveDocument } from '@contracts/save';
import { isKnownGame } from '@platform/game-registry';
import { announce } from '@platform/announce';
import type { LoadFailure } from '@platform/levels';
import { loadLevel } from '@platform/levels';
import { LeaveConfirmDialog } from '@shell/LeaveConfirmDialog';
import { Screen } from '@shell/Screen';
import { routes } from '@shell/routes';
import { NotFound } from '@shell/screens/NotFound';
import { loadFailureMessage } from '@shell/load-failure-message';
import { currentDocument, persist } from '@shell/save';
import { useGameModule } from '@shell/use-game-module';
import type { Level } from '@contracts/level';

export function Play({ gameId, levelId }: { gameId: string; levelId: string }) {
  if (!isKnownGame(gameId)) return <NotFound />;
  return <PlayLoaded gameId={gameId} levelId={levelId} />;
}

type LevelState =
  | { status: 'loading' }
  | { status: 'ready'; level: Level }
  | { status: 'error'; failure: LoadFailure };

/** Not yet looked at the save document -- distinct from "looked, and it was `null`". */
const UNCHECKED = Symbol('in-progress not yet checked');

function PlayLoaded({ gameId, levelId }: { gameId: string; levelId: string }) {
  const moduleState = useGameModule(gameId);
  const readyGame = moduleState.status === 'ready' ? moduleState.game : undefined;
  const readyIndex = moduleState.status === 'ready' ? moduleState.index : undefined;

  const [levelState, setLevelState] = useState<LevelState>({ status: 'loading' });
  const [result, setResult] = useState<GameResult | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  // DATA-031/DATA-032: fetch and validate the one level about to be played,
  // and no other. `readyIndex` only carries entries, not files, so the file
  // this game emitted for `levelId` still has to be looked up before
  // `loadLevel` can fetch anything.
  useEffect(() => {
    if (!readyGame || !readyIndex) return;

    const entry = readyIndex.levels.find((candidate) => candidate.id === levelId);
    if (!entry) {
      setLevelState({
        status: 'error',
        failure: { cause: 'missing', detail: `game ${gameId} has no level named ${levelId}` },
      });
      return;
    }

    let cancelled = false;
    setLevelState({ status: 'loading' });
    void loadLevel(readyGame, entry.file).then((loaded) => {
      if (cancelled) return;
      setLevelState(
        loaded.ok
          ? { status: 'ready', level: loaded.value }
          : { status: 'error', failure: loaded.failure },
      );
    });

    return () => {
      cancelled = true;
    };
  }, [readyGame, readyIndex, gameId, levelId]);

  const readyLevel = levelState.status === 'ready' ? levelState.level : undefined;

  // PROG-030/034/035: the save document's one global in-progress slot, read
  // exactly once -- see the file header for why a ref rather than a fresh
  // read every render.
  const initialInProgressRef = useRef<InProgress | null | typeof UNCHECKED>(UNCHECKED);
  if (readyLevel && initialInProgressRef.current === UNCHECKED) {
    initialInProgressRef.current = currentDocument().inProgress;
  }
  const initialInProgress =
    initialInProgressRef.current === UNCHECKED ? null : initialInProgressRef.current;
  const resumableHere =
    initialInProgress?.gameId === gameId && initialInProgress.levelId === levelId
      ? initialInProgress
      : null;
  const otherInProgress = initialInProgress && !resumableHere ? initialInProgress : null;

  const [resumeChoice, setResumeChoice] = useState<'pending' | 'resume' | 'fresh'>('pending');
  const [restoreFailed, setRestoreFailed] = useState(false);
  // SHELL-017's dialog. Holds the two answers as closures built by the
  // popstate handler below, since that is where `playUrl`, `phantomLive` and
  // `persistInProgress` live -- `null` means no confirmation is showing.
  const [leaveConfirm, setLeaveConfirm] = useState<{
    onStay: () => void;
    onLeave: () => void;
  } | null>(null);

  const awaitingOffer = Boolean(resumableHere) && resumeChoice === 'pending';
  const awaitingWarning = Boolean(otherInProgress) && resumeChoice === 'pending';

  // Mounts exactly once per (game, level) pair that becomes ready, and only
  // once any resume offer or discard warning above has been answered. A throw
  // from `game.mount()` -- `goal.ts` on a level with no custom goal is the
  // known case -- happens here, inside an effect, and Preact hands it to the
  // nearest `ErrorBoundary` (SHELL-006) the same way a render throw would --
  // except on a resume attempt specifically, PROG-032's territory, handled
  // below rather than left to that boundary.
  useEffect(() => {
    if (!readyGame || !readyLevel) return;
    if (awaitingOffer || awaitingWarning) return;
    const host = hostRef.current;
    if (!host) return;
    const level = readyLevel;

    let torn = false;
    let teardown: () => void = () => undefined;
    // PROG-031's "state most recently reported" -- kept here rather than in a
    // signal because nothing outside this effect needs it: the backgrounding
    // and back-navigation handlers below are the only readers, in the same
    // closure.
    let latestState: unknown;

    // SHELL-017's guard, pushed now so it is in place before buildCtx below is
    // ever reached -- see the popstate listener further down for what it is
    // for. `phantomLive` tracks whether it is still sitting in the history
    // stack unconsumed, since two different paths can consume it: a real back
    // press (the listener), or the level simply ending (`onComplete`, just
    // below) while it was never pressed at all. Left live, a level that ends
    // by finishing rather than by backing out would leave it stranded behind
    // whatever the player navigates to next, silently absorbing that
    // screen's *next* back press instead of the one it was meant to guard.
    const playUrl = location.url;
    history.pushState(null, '', playUrl);
    let phantomLive = true;

    const clearInProgressIfThisLevel = (document: SaveDocument): SaveDocument =>
      document.inProgress?.gameId === gameId && document.inProgress.levelId === levelId
        ? { ...document, inProgress: null }
        : document;

    function buildCtx(savedState: unknown): GameContext {
      return {
        level,
        savedState,
        announce,
        onStateChange: (state) => {
          latestState = state;
        },
        onComplete: (outcome: GameResult) => {
          if (torn) return;
          torn = true;
          // SHELL-047: unmounted before the result is shown, not alongside it.
          teardown();
          // PROG-033: a level that has ended, by any outcome, has nothing left
          // to resume.
          void persist(clearInProgressIfThisLevel);
          if (phantomLive) {
            // Consumed silently: the resulting popstate lands on `onPopState`
            // below with `torn` already true, so it neither asks nor moves
            // anything -- this only retires the guard before it can outlive
            // the level it belonged to.
            phantomLive = false;
            history.back();
          }
          setResult(outcome);
        },
      };
    }

    const savedState = resumeChoice === 'resume' ? resumableHere?.state : undefined;

    try {
      teardown = readyGame.mount(host, buildCtx(savedState));
    } catch (error) {
      if (savedState === undefined) {
        // A genuine mount failure unrelated to resuming -- SHELL-006's job,
        // the same as before this stage.
        throw error;
      }
      // PROG-032. The resume attempt itself is what failed, not the level in
      // general: falling through to SHELL-006's boundary here would strand
      // the player behind a generic error with no way back to this exact
      // level, for a level that plays fine from the start. Retried once, not
      // wrapped in its own try/catch -- a level that fails to mount at all is
      // exactly the case above, and must still reach the boundary.
      setRestoreFailed(true);
      teardown = readyGame.mount(host, buildCtx(undefined));
    }

    // PROG-005. Neither event alone covers every platform: `visibilitychange`
    // is what fires when a player backgrounds the app to take a call, but iOS
    // Safari can discard a backgrounded tab without ever firing it again, and
    // `pagehide` is what catches an actual navigation away or tab close --
    // including the ones bfcache later restores from, where `beforeunload`
    // does not fire reliably at all. Both funnel through the same save.
    const persistInProgress = (): void => {
      // A level that has already ended has nothing "in progress" to save --
      // `onComplete` already tore the game down, and writing a snapshot after
      // that would resurrect a finished level as resumable.
      if (torn) return;
      void persist((saved) => ({
        ...saved,
        inProgress: {
          gameId,
          levelId,
          state: latestState,
          savedAt: new Date().toISOString(),
        },
      }));
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'hidden') return;
      persistInProgress();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', persistInProgress);

    // SHELL-017. `popstate`, not `beforeunload`: this is specifically about
    // the router's own back navigation within the app, and `beforeunload`
    // does not fire for a same-document route change at all.
    //
    // `popstate` cannot be prevented, and cannot reliably be raced either --
    // preact-iso's own listener (registered once, at boot, ahead of this one)
    // always processes it first. Racing it by blocking the main thread while
    // asking turned out not to hold in a real browser the way it does in
    // theory, dialog or not; what actually works is the phantom history entry
    // pushed above, which turns the *first* back press into a no-op (an
    // identical-URL `popstate`, which preact-iso's own reducer bails out of
    // re-rendering for) rather than something that has to be raced or undone.
    // That non-event is this handler's cue to ask, via the dialog rather than
    // `window.confirm` (SHELL-019, and see LeaveConfirmDialog.tsx for why).
    // Staying re-arms the guard for next time. Leaving does not try to replay
    // whatever "back" would have reached -- it routes straight to level
    // select, the same destination every other way out of this screen
    // already uses (DATA-040, the module-load error, the result screen).
    const onPopState = (): void => {
      if (torn) return;
      phantomLive = false;
      setLeaveConfirm({
        onStay: () => {
          history.pushState(null, '', playUrl);
          phantomLive = true;
          setLeaveConfirm(null);
        },
        onLeave: () => {
          persistInProgress();
          setLeaveConfirm(null);
          location.route(routes.levelSelect(gameId), true);
        },
      });
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', persistInProgress);
      window.removeEventListener('popstate', onPopState);
      if (torn) return;
      torn = true;
      teardown();
    };
    // `resumableHere` and `location` are deliberately not in this list:
    // `resumableHere` only changes identity because `resumeChoice` (already
    // listed) changed, and `location` is preact-iso's own stable object for
    // the current app session.
  }, [readyGame, readyLevel, gameId, levelId, resumeChoice, awaitingOffer, awaitingWarning]);

  if (moduleState.status === 'loading') {
    return (
      <Screen title="Play">
        <p>Loading&hellip;</p>
      </Screen>
    );
  }

  if (moduleState.status === 'error') {
    return (
      <Screen title="Play">
        <p>{loadFailureMessage(moduleState.failure)}</p>
        <nav class="screen-nav">
          <button type="button" onClick={moduleState.retry}>
            Try again
          </button>
          <a class="nav-link" href={routes.picker()}>
            Back to the game picker
          </a>
        </nav>
      </Screen>
    );
  }

  if (levelState.status === 'loading') {
    return (
      <Screen title="Play">
        <p>Loading the level&hellip;</p>
      </Screen>
    );
  }

  if (levelState.status === 'error') {
    // DATA-040: report the level as unloadable and offer the way back level
    // select requires, rather than leaving the player stranded on this route.
    return (
      <Screen title="Play">
        <p>{loadFailureMessage(levelState.failure)}</p>
        <nav class="screen-nav">
          <a class="nav-link" href={routes.levelSelect(gameId)}>
            Back to level select
          </a>
        </nav>
      </Screen>
    );
  }

  if (result) {
    return (
      <Screen title="Play">
        <p>{outcomeMessage(result)}</p>
        <nav class="screen-nav">
          <a class="nav-link" href={routes.levelSelect(gameId)}>
            Back to level select
          </a>
        </nav>
      </Screen>
    );
  }

  // PROG-030.
  if (awaitingOffer) {
    return (
      <Screen title="Play">
        <p>You have progress saved here. Resume where you left off?</p>
        <nav class="screen-nav">
          <button
            type="button"
            onClick={() => {
              setResumeChoice('resume');
            }}
          >
            Resume
          </button>
          <button
            type="button"
            onClick={() => {
              void persist((document) =>
                document.inProgress?.gameId === gameId && document.inProgress.levelId === levelId
                  ? { ...document, inProgress: null }
                  : document,
              );
              setResumeChoice('fresh');
            }}
          >
            Start fresh
          </button>
        </nav>
      </Screen>
    );
  }

  // PROG-035.
  if (awaitingWarning) {
    return (
      <Screen title="Play">
        <p>
          Starting this level will discard your saved progress in another level. That progress will
          be lost.
        </p>
        <nav class="screen-nav">
          <button
            type="button"
            onClick={() => {
              setResumeChoice('fresh');
            }}
          >
            Continue
          </button>
          <a class="nav-link" href={routes.levelSelect(gameId)}>
            Cancel
          </a>
        </nav>
      </Screen>
    );
  }

  return (
    <Screen title="Play">
      {restoreFailed && (
        <p class="save-notice">Your saved progress here could not be restored. Starting fresh.</p>
      )}
      <div ref={hostRef} class="play-host" />
      {leaveConfirm && (
        <LeaveConfirmDialog onStay={leaveConfirm.onStay} onLeave={leaveConfirm.onLeave} />
      )}
    </Screen>
  );
}

function outcomeMessage(result: GameResult): string {
  switch (result.outcome) {
    case 'won':
      return 'You won!';
    case 'lost':
      return 'Level over.';
    case 'abandoned':
      return 'You left the level.';
  }
}
